import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Modal,
  Loader,
  Button,
  Group,
  Container,
  Tooltip,
  Box,
} from "@mantine/core";
import moment from "moment-timezone";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../../types/transactions";
import {
  SelectedService,
  ServiceSchedule,
} from "../../../../../../types/availability";
import { CalendarEventType } from "../../../../../../types/calendar";
import { useSalon } from "../../../../../../context/SalonContext";
import { useApp } from "../../../../../../context/AppContext";
import { useCalendar } from "../../../../../../context/CalendarContext";
import { useTransaction } from "../../../../../../context/TransactionContext";
import { useCalendarEvent } from "../../../../../../context/CalendarEventContext";
import {
  transformTransactionToEvent,
  transformTransactionResponseToGetTransactionsResponse,
} from "../../../../../../utils/transactionUtils";
import { notifyServiceBookingError } from "../../../../../../utils/serviceAvailabilityError";
import { getCreatedTransactions } from "../../../../../../utils/bookingResponse";
import { getSalonNow } from "../../Views/sharedCalendarUtils";
import ConfigureServicesStep from "./ConfigureServicesStep";
import ScheduleServicesStep from "./ScheduleServicesStep";
import ConfirmRebookingStep from "./ConfirmRebookingStep";
import SuccessStep from "./SuccessStep";
import styles from "./RebookModal.module.css";

interface RebookModalProps {
  opened: boolean;
  onClose: () => void;
  transaction: GetTransactionsResponse | TransactionResponse;
  onViewNewAppointment: (transactionId: string) => void;
  /** Entry point: "popover" (from the calendar event preview) or
   * "transactionModal" (from the transaction detail modal). Controls the
   * success-screen secondary button and where the calendar lands on close. */
  source?: "popover" | "transactionModal";
}

export default function RebookModal({
  opened,
  onClose,
  transaction,
  onViewNewAppointment,
  source = "transactionModal",
}: RebookModalProps) {
  const {
    businessInfo,
    services: salonServices,
    refreshServices,
  } = useSalon() as any;
  const { createAppointment } = useApp();
  const { addTransaction } = useTransaction();
  const {
    selectedDate,
    setSelectedDate,
    viewType,
    setViewType,
    selectedArtists,
    setSelectedArtists,
    selectedServiceTypes,
    setSelectedServiceTypes,
    selectedStatuses,
    setSelectedStatuses,
  } = useCalendar();
  const { setEvents } = useCalendarEvent() as {
    setEvents: React.Dispatch<React.SetStateAction<CalendarEventType[]>>;
  };

  // Step management: 1 = Configure Services, 2 = Schedule Services, 3 = Confirm, 4 = Success
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    [],
  );
  const [serviceSchedules, setServiceSchedules] = useState<
    Record<string, ServiceSchedule>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [newTransactionId, setNewTransactionId] = useState<string | null>(null);

  // The scheduling step (step 2) reuses the app's live Day/Week calendar, driven
  // by the shared CalendarContext. We snapshot the main calendar's date/view AND
  // its filters when the modal opens and restore them when it closes, so the
  // scheduling calendar never leaks its state (e.g. the per-service staff filter)
  // onto the main calendar. `liveRef` mirrors the latest context values + setters
  // so the open/close effect can read them without re-subscribing (a changing
  // setter identity must not trigger a spurious mid-session restore).
  const liveRef = useRef({
    selectedDate,
    viewType,
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    setSelectedDate,
    setViewType,
    setSelectedArtists,
    setSelectedServiceTypes,
    setSelectedStatuses,
    timezone: businessInfo?.timezone as string | undefined,
    source,
  });
  liveRef.current = {
    selectedDate,
    viewType,
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    setSelectedDate,
    setViewType,
    setSelectedArtists,
    setSelectedServiceTypes,
    setSelectedStatuses,
    timezone: businessInfo?.timezone,
    source,
  };

  // Initialize selected services from transaction's booked services
  useEffect(() => {
    if (opened && transaction.line_items.booked_services.length > 0) {
      const initialServices: SelectedService[] =
        transaction.line_items.booked_services.map((service) => {
          // Calculate total price including service adjustment form option deltas
          // unit_price is the base price, and we need to add the price_delta from each sub_line_item
          const totalPriceDelta =
            service.sub_line_items?.reduce(
              (sum, item) => sum + (item.price_delta || 0),
              0,
            ) || 0;
          const totalPrice = service.unit_price + totalPriceDelta;

          // Calculate total base duration including service adjustment form option deltas
          // base_duration_mins is the base duration, and we need to add duration_delta_mins from each sub_line_item
          const totalDurationDelta =
            service.sub_line_items?.reduce(
              (sum, item) => sum + (item.duration_delta_mins || 0),
              0,
            ) || 0;

          return {
            id: service.id,
            staffServiceId: service.staff_service_id,
            salonServiceId: service.salon_service_id,
            staffId: service.staff_id,
            staffName: service.staff_name,
            serviceName: service.name,
            price: totalPrice,
            totalDurationDelta: totalDurationDelta,
            baseDuration: service.base_duration_mins || 0,
            processingTime: service.processing_time_mins || 0,
            finishingTime: service.finishing_time_mins || 0,
            bufferTime: service.buffer_time_mins || 0,
            isNew: false,
            serviceAdjustmentFormOptionIds: service.sub_line_items?.map(
              (item) => item.source_id,
            ),
          };
        });
      setSelectedServices(initialServices);
    }
  }, [opened, transaction.line_items.booked_services]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setStep(1);
      setSelectedServices([]);
      setServiceSchedules({});
      setIsLoading(false);
      setNewTransactionId(null);
    }
  }, [opened]);

  // Drop services no longer in the catalog (deleted then dropped by
  // refreshServices) so a re-submit can't re-send a dead staff_service_id.
  useEffect(() => {
    if (!salonServices) return;
    setSelectedServices((prev) => {
      const pruned = prev.filter((s) =>
        salonServices.some((svc: any) => svc.id === s.salonServiceId),
      );
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [salonServices]);

  // Snapshot the main calendar's date/view/filters when the modal opens; restore
  // them when it closes OR unmounts. The restore lives in effect CLEANUP because
  // the popover instance is unmounted on close (its parent clears the transaction
  // to rebook), so a body-based restore would never run and the staff filter would
  // leak onto the main calendar. Popover entry lands on Today; the transaction-
  // modal entry restores the date it came from.
  useEffect(() => {
    if (!opened) return;
    const l = liveRef.current;
    const snap = {
      date: l.selectedDate,
      view: l.viewType,
      artists: l.selectedArtists,
      serviceTypes: l.selectedServiceTypes,
      statuses: l.selectedStatuses,
    };
    return () => {
      const c = liveRef.current;
      c.setSelectedDate(
        c.source === "popover" ? getSalonNow(c.timezone) : snap.date,
      );
      c.setViewType(snap.view);
      c.setSelectedArtists(snap.artists);
      c.setSelectedServiceTypes(snap.serviceTypes);
      c.setSelectedStatuses(snap.statuses);
    };
  }, [opened]);

  // On entering the scheduling step, ensure the calendar is on a usable day/week
  // view (staff columns require "day"); the original view is restored on close.
  useEffect(() => {
    const l = liveRef.current;
    if (opened && step === 2 && l.viewType !== "day" && l.viewType !== "week") {
      l.setViewType("day");
    }
  }, [opened, step]);

  // Schedule (or reschedule) a single service. Staff is kept from step 1, so the
  // schedule's staff_service_id always matches the service's own. No auto-advance.
  const handleScheduleService = useCallback(
    (serviceId: string, date: string, time: string) => {
      const service = selectedServices.find((s) => s.id === serviceId);
      if (!service) return;
      setServiceSchedules((prev) => ({
        ...prev,
        [serviceId]: {
          serviceId,
          date,
          time,
          staffServiceId: service.staffServiceId,
        },
      }));
    },
    [selectedServices],
  );

  // Clear a single service's chosen time.
  const handleClearSchedule = useCallback((serviceId: string) => {
    setServiceSchedules((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  }, []);

  // Every service must have a time before the user can review the booking.
  const allScheduled =
    selectedServices.length > 0 &&
    selectedServices.every((s) => !!serviceSchedules[s.id]);

  // Handle confirm rebooking
  const handleConfirmRebooking = useCallback(async () => {
    setIsLoading(true);
    try {
      const timezone = businessInfo?.timezone || "America/New_York";

      // Build appointment payload
      const appointmentPayload = {
        booked_services: selectedServices.map((service) => {
          const schedule = serviceSchedules[service.id];

          // Correctly parse date and time in the business timezone
          // This matches the pattern used in TransactionBookedServiceModal
          const startDateTime = moment
            .tz(schedule.date, "YYYY-MM-DD", timezone)
            .hour(moment(schedule.time, "HH:mm").hour())
            .minute(moment(schedule.time, "HH:mm").minute())
            .toDate();

          return {
            staff_service_id: schedule.staffServiceId,
            start_dt: startDateTime.toISOString(),
            unit_price: service.price,
            base_duration_mins: service.baseDuration,
            processing_time_mins: service.processingTime,
            finishing_time_mins: service.finishingTime,
            buffer_time_mins: service.bufferTime,
            service_adjustment_form_option_ids:
              service.serviceAdjustmentFormOptionIds,
          };
        }),
        source: "AP - Staff Rebooking",
        client_uuid: transaction.client.id,
        rebooking_source_uuid: transaction.transaction.id,
      };

      const response = await createAppointment(appointmentPayload);

      // Day-partitioned create: a same-day rebook returns one transaction; a
      // multi-day rebook returns { transactions: [...] }. Normalize to a list
      // and add every created transaction to the list + calendar.
      const created = getCreatedTransactions(response);
      if (created.length) {
        created.forEach((txnResponse) => {
          const newTransaction =
            transformTransactionResponseToGetTransactionsResponse(txnResponse);
          addTransaction(newTransaction);

          const newEvents = transformTransactionToEvent(
            txnResponse,
            businessInfo?.timezone || "",
            salonServices,
          );
          setEvents(
            (prevEvents: CalendarEventType[]) =>
              [...prevEvents, ...newEvents] as CalendarEventType[],
          );
        });

        // Focus the first created transaction on the success step.
        setNewTransactionId(created[0].transaction.id);
        setStep(4);
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      notifyServiceBookingError(error, {
        refreshServices,
        fallbackMessage:
          "We couldn't complete the rebooking. Please try again.",
        title: "Rebooking failed",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedServices,
    serviceSchedules,
    transaction,
    createAppointment,
    businessInfo,
    addTransaction,
    setEvents,
  ]);

  // Handle back button click
  const handleBack = useCallback(() => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  }, [step]);

  // Handle next button click
  const handleNext = useCallback(() => {
    if (step === 1) {
      // Proceed from Configure Services to Schedule Services
      if (selectedServices.length > 0) {
        setStep(2);
      }
    } else if (step === 2) {
      // All services scheduled -> review
      setStep(3);
    } else if (step === 3) {
      // Trigger confirm rebooking
      handleConfirmRebooking();
    }
  }, [step, selectedServices.length, handleConfirmRebooking]);

  // Handle view new appointment
  const handleViewNewAppointment = useCallback(() => {
    if (newTransactionId) {
      onClose();
      onViewNewAppointment(newTransactionId);
    }
  }, [newTransactionId, onClose, onViewNewAppointment]);

  // Handle stay here
  const handleStayHere = useCallback(() => {
    onClose();
  }, [onClose]);

  // Determine step header text
  const getStepHeader = () => {
    if (step === 1) return "Select Services (1/3)";
    else if (step === 2) return "Schedule Services (2/3)";
    else if (step === 3) return "Confirm Rebooking (3/3)";
    else {
      return null; // No step header for success
    }
  };

  // Determine if back button should be shown
  const showBackButton = step > 1 && step < 4;

  // Determine if next button should be shown
  const showNextButton = step === 1 || step === 2 || step === 3;

  // Determine next button label
  const getNextButtonLabel = () => {
    if (step === 1) return "Next: Schedule Services";
    if (step === 2) return "Next: Review Booking";
    if (step === 3) return "Confirm Rebooking";
    return "Next";
  };

  // Determine if next button should be disabled
  const isNextDisabled = () => {
    if (step === 1) return selectedServices.length === 0;
    if (step === 2) return !allScheduled;
    if (step === 3) return isLoading;
    return false;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={getStepHeader()}
      styles={{
        // Bound the modal to the viewport and hide its own scroll so the inner
        // calendar (not the modal body) scrolls — Mantine's content defaults to
        // overflow-y: auto, which would otherwise scroll the whole modal.
        // Explicit white background: the content override otherwise renders
        // transparent over the (now gray) transaction modal behind it.
        content: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        },
        header: { backgroundColor: "#ffffff" },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: 0,
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        },
      }}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <Loader color="teal" size="lg" />
        </div>
      )}

      {/* Step Content — fills the body (above the pinned footer). The scheduling
          step owns the full-screen split layout; the other steps render in a
          centered, scrollable column. */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {step === 2 ? (
          <ScheduleServicesStep
            selectedServices={selectedServices}
            serviceSchedules={serviceSchedules}
            onScheduleService={handleScheduleService}
            onClearSchedule={handleClearSchedule}
            salonServices={salonServices}
            client={transaction.client}
          />
        ) : step === 4 ? (
          // Success — centered horizontally and vertically, but biased toward the
          // top (extra bottom padding lifts the stack above true center).
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--mantine-spacing-md)",
              paddingBottom: "18dvh",
            }}
          >
            <SuccessStep
              source={source}
              onViewNewAppointment={handleViewNewAppointment}
              onStayHere={handleStayHere}
            />
          </Box>
        ) : (
          <Container
            size="md"
            w="100%"
            p="md"
            style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
          >
            {step === 1 && (
              <ConfigureServicesStep
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
              />
            )}

            {step === 3 && (
              <ConfirmRebookingStep
                selectedServices={selectedServices}
                serviceSchedules={serviceSchedules}
              />
            )}
          </Container>
        )}
      </Box>

      {/* Navigation Footer - Only show for steps 1-3 */}
      {step < 4 && (
        <Group
          justify="space-between"
          className={styles.navigationFooter}
          style={{ flexShrink: 0 }}
        >
          {showBackButton ? (
            <Button
              variant="subtle"
              color="gray"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          ) : (
            <div /> // Empty div for spacing
          )}

          {showNextButton && (
            <Tooltip
              label="Schedule every service to continue"
              disabled={!(step === 2 && isNextDisabled())}
              position="top"
              withArrow
            >
              <Button
                color="var(--color-primary)"
                onClick={handleNext}
                disabled={isNextDisabled()}
                loading={isLoading && step === 3}
              >
                {getNextButtonLabel()}
              </Button>
            </Tooltip>
          )}
        </Group>
      )}
    </Modal>
  );
}
